import { FC, useCallback, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import SelectSignupType from '@/auth/components/SelectSignupType';
import { SelfServiceSMBSignupModal } from '@/auth/components/SelfServiceSMBSignupModal';
import { useSelfServiceSMBSignupModal } from '@/auth/components/SelfServiceSMBSignupModal/hooks/useSelfServiceSMBSignupModal';
import { appendSignupParams } from '@/auth/utils/signupParams';
import LayoutPage from '@/components/Layout/LayoutPage';

interface CustomerRegisterPageProps {
  marketingLinks: string[];
  subdomain?: string;
  nextUrl?: string;
}

/**
 * /signup/customer entry point for the self-service SMB signup flow.
 *
 * Renders the /signup choice screen (SelectSignupType) heavily blurred
 * behind the modal as visual context for anonymous direct landings — this
 * is the one entry point where a synthetic backdrop is appropriate (the
 * user is here to sign up, so the choice screen IS the context). Every
 * other entry point (dashboard banners, expert profile CTAs, /request_expert
 * gate) mounts the modal inline on its own page via `useSelfServiceSMBSignupModal`
 * so the actual page becomes the blurred backdrop.
 *
 * /signup/member retains the legacy Client Signup form as a hard rollback path.
 */
const CustomerRegisterPage: FC<CustomerRegisterPageProps> = ({
  marketingLinks,
  subdomain,
  nextUrl,
}) => {
  const navigate = useNavigate();
  const { search } = useLocation();
  const [open, setOpen] = useState(true);

  const { modalProps } = useSelfServiceSMBSignupModal({ nextUrl });

  // Carry campaign params back to the chooser, or dismissing the modal and
  // clicking back in silently drops the trial (ENG-5527).
  const handleClose = useCallback(() => {
    setOpen(false);
    navigate(appendSignupParams('/signup', search));
  }, [navigate, search]);

  return (
    <LayoutPage headerBorder={false} marketingLinks={marketingLinks}>
      <SelectSignupType subdomain={subdomain} nextUrl={nextUrl} />
      <SelfServiceSMBSignupModal {...modalProps} open={open} onClose={handleClose} />
    </LayoutPage>
  );
};

export default CustomerRegisterPage;
